import { Subject, ReplaySubject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { isEqual } from 'lodash';

import { Injectable } from '@angular/core';

import { ServerApi } from '@services/server-api';

import {
  IGlobalSupport,
  IGlobalSupportServer,
} from './global-support.interface';
import { extractResponseBody } from '@lib/utils';
import { getTimezoneWithGMTOffset } from '@lib/time-zones';

import { LOCAL_STORAGE_KEY } from './global-support.const';

@Injectable()
export class BaseService {
  protected timeZoneCountryData$: ReplaySubject<IGlobalSupport> = new ReplaySubject();
  private destroy$ = new Subject<void>();

  constructor(private api: ServerApi) {
    this.fetchData();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fetchData(): void {
    const loadedData: IGlobalSupport = this.getDataFromLocalStorage();
    if (loadedData) {
      this.timeZoneCountryData$.next(loadedData);
    }
    this.api.get<IGlobalSupportServer>('sessions/timezone_country_config')
      .pipe(
        takeUntil(this.destroy$),
        map(extractResponseBody),
        map(this.transformData),
      )
      .subscribe(data => {
        if (this.shouldUpdateLocalStorage(loadedData, data)) {
          this.timeZoneCountryData$.next(data);
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
        }
      });
  }

  private transformData(data: IGlobalSupportServer): IGlobalSupport {
    return {
      updatedAt: data.updated_at,
      countries: data.countries.map(country => ({
        name: country.name,
        code: country.code,
        phoneCode: country.phone_code,
        phoneNumberValidator: country.phone_number_validator,
        phoneNumberMask: country.phone_number_mask,
        countryCodeLabel: `(${country.phone_code}) ${country.name}`,
      })),
      states: data.countries.reduce((acc, country) => {
        acc[country.code] = country.states.map(state => ({...state }));
        return acc;
      }, {}),
      timeZones: data.time_zones.map(timeZone => ({
        ianaIdentifier: timeZone.iana_identifier,
        standardName: timeZone.standard_name,
        supportsDst: timeZone.supports_dst,
        backendTimeZone: timeZone.backend_time_zone,
        localizedStandardName: timeZone.localized_standard_name,
        localizedStandardNameWithGMTOffset: getTimezoneWithGMTOffset(timeZone),
      })),
      minAppVersion: data.iana_time_zone_min_app_version ? Number(data.iana_time_zone_min_app_version.split('.')[0]) : 0,
    };
  }

  private getDataFromLocalStorage(): IGlobalSupport {
    try {
      const localTimeZoneCountryData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (localTimeZoneCountryData) {
        return JSON.parse(localTimeZoneCountryData);
      }
    } catch (e) {
      return null;
    }
  }

  private shouldUpdateLocalStorage(oldData: IGlobalSupport, newData: IGlobalSupport): boolean {
    if (!oldData) {
      return true;
    }
    return !isEqual(oldData, newData);
  }
}
